// Industrial service data — consumed by <ServiceDetail track="industrial" />.
// Hero and material images are photorealistic Higgsfield-generated stock
// photos (frontend/public/images/industrial/), one per service/material.

export const INDUSTRIAL_SERVICES_DATA = {
    "heavy-duty-asphalt-paving": {
        slug: "heavy-duty-asphalt-paving",
        category: "Industrial · Asphalt & Paving",
        name: "Heavy-Duty Asphalt & Paving",
        heroTitle: "Asphalt built for axle loads, not foot traffic.",
        heroSubhead:
            "Heavy-duty paving engineered for trucking yards, loading aprons, and industrial lots across the GTA — rated for the loads you actually run.",
        heroImage: "/images/industrial/heavy-duty-asphalt-paving-hero.0a99a144.webp",
        heroImageAlt: "Skid steer dumping hot asphalt as a crew places a new mat alongside a large brick institutional building.",
        intro:
            "Industrial pavement fails from the base up, not the surface down. We engineer the granular base and asphalt mat to the wheel loads, turning radii, and drainage of your site — so loaded trailers, forklifts, and container handlers don't rut or alligator the surface in their first season. Every scope is built to minimize operational downtime and documented for your facility records.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Load-rated structural design",
                body: "Base depth and asphalt mat thickness specified to your axle loads and traffic counts, not a generic residential spec.",
            },
            {
                icon: "ShieldCheck",
                title: "Compaction-tested base",
                body: "Granular sub-base placed and compacted in lifts with density verified before paving — the failure point we see most on competitor work.",
            },
            {
                icon: "Clock",
                title: "Phased, low-downtime execution",
                body: "We sequence and stage the work so your yard keeps moving freight while sections cure and open.",
            },
            {
                icon: "Award",
                title: "Documented sign-off",
                body: "Mix tickets, compaction records, and as-built notes handed over for your asset and warranty files.",
            },
        ],
        materials: [
            {
                name: "Engineered base build",
                desc: "Granular A and B placed and compacted in lifts, graded to drainage targets before any asphalt is laid.",
                image: "/images/industrial/heavy-duty-asphalt-paving-m1.5d876ea0.webp",
                alt: "Compacted granular base being rolled to grade with string lines before asphalt is laid",
            },
            {
                name: "Heavy-duty asphalt mat",
                desc: "Multi-lift binder and surface course matched to axle loads, with thickness specified for your traffic.",
                image: "/images/industrial/heavy-duty-asphalt-paving-m2.88cbf84b.webp",
                alt: "Paver laying a thick hot asphalt lift with a roller compacting close behind",
            },
            {
                name: "Line marking & traffic flow",
                desc: "Durable thermoplastic or paint layout for truck routes, loading bays, fire lanes, and accessible stalls.",
                image: "/images/industrial/heavy-duty-asphalt-paving-m3.48d3a660.webp",
                alt: "Yellow stall lines and a directional chevron marked on asphalt beside an industrial loading-dock unit.",
            },
            {
                name: "Sealing & joint maintenance",
                desc: "Crack routing, sealing, and surface treatment programs to extend pavement life between full resurfacing.",
                image: "/images/industrial/heavy-duty-asphalt-paving-m4.cf41a052.webp",
                alt: "Routed pavement crack filled with rubberised sealant beside an untreated crack",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We walk the site, measure areas, take core samples where needed, and document load patterns, drainage, and existing failures." },
            { num: "02", title: "Engineered Scope & Quote", body: "A line-item scope with base depths, mat thickness, and a fixed price engineered to your traffic loads." },
            { num: "03", title: "Scheduled Execution", body: "Work phased around your operations and shift windows so freight keeps moving with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Mix tickets, compaction records, and as-built notes delivered for your facility and warranty files." },
        ],
        faqs: [
            {
                q: "What's the difference between industrial and standard parking-lot asphalt?",
                a: "Industrial paving is engineered for sustained heavy axle loads — loaded trailers, container handlers, forklifts — not occasional car traffic. That means a deeper, properly compacted granular base, a thicker multi-lift asphalt mat, and a mix designed to resist rutting at the slow speeds and pivot points common in trucking yards. We spec the structure to your actual loads rather than a generic depth.",
            },
            {
                q: "How long will my industrial lot be out of service during paving?",
                a: "It depends on site size and whether we can phase the work, but most facilities never fully close. We sequence the lot into sections, keep truck routes open, and time pours and cures around your shift windows. We'll lay out the staging plan and per-section downtime in the proposal so your operations team can plan around it.",
            },
            {
                q: "Why does heavy-duty asphalt rut or fail early?",
                a: "Almost always the base. If the granular sub-base isn't placed in lifts and compacted to density, heavy loads push it down and the surface follows — you get ruts, depressions, and alligator cracking. Standing water from poor grading accelerates it. We verify compaction before paving and grade to drainage targets, which is why we document those steps.",
            },
            {
                q: "Are you insured and WSIB-compliant for work on our site?",
                a: "Yes. We carry commercial general liability and provide a Certificate of Insurance naming your entity, and we're WSIB-registered with clearance certificates available on request. For industrial sites we'll also align with your site-specific safety and contractor-orientation requirements before mobilizing.",
            },
            {
                q: "Do you handle multiple sites across the GTA under one contract?",
                a: "Yes. We work across Brampton, Mississauga, Toronto, and the wider GTA and regularly handle multi-site portfolios under a single point of contact, with consistent specs and consolidated documentation across locations.",
            },
        ],
    },

    "industrial-concrete": {
        slug: "industrial-concrete",
        category: "Industrial · Concrete",
        name: "Industrial Concrete",
        heroTitle: "Concrete that takes the load and keeps its level.",
        heroSubhead:
            "Slabs, loading aprons, and equipment pads engineered for industrial point loads, traffic, and the freeze-thaw cycles of an Ontario winter.",
        heroImage: "/images/industrial/industrial-concrete-hero.d4edaacc.webp",
        heroImageAlt: "Cast-in-place concrete foundation walls and columns with projecting rebar on an active construction site.",
        intro:
            "Industrial concrete carries point loads, dynamic traffic, and thermal movement that residential slabs never see. We design the slab thickness, reinforcement, and joint layout to your equipment and traffic, then place and cure to spec so the surface holds tolerance and resists spalling. Every pour is documented for your asset records.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Reinforced, load-rated slabs",
                body: "Thickness, rebar, and mesh designed to your point loads and rack legs — not a one-size pad.",
            },
            {
                icon: "ShieldCheck",
                title: "Freeze-thaw durable mixes",
                body: "Air-entrained, spec-strength mixes that resist scaling and spalling through GTA winters.",
            },
            {
                icon: "Award",
                title: "Engineered joint layout",
                body: "Control and isolation joints placed to manage cracking and protect equipment alignment.",
            },
            {
                icon: "Clock",
                title: "Cure-aware scheduling",
                body: "Pours staged and cured around your operations so areas reopen on a predictable timeline.",
            },
        ],
        materials: [
            {
                name: "Heavy-duty floor slabs",
                desc: "Reinforced interior and exterior slabs designed for forklift traffic, racking, and point loads.",
                image: "/images/industrial/industrial-concrete-m1.a1028054.webp",
                alt: "Crew placing concrete from a boom pump for a large slab inside cast-in-place foundation walls.",
            },
            {
                name: "Loading docks & aprons",
                desc: "Reinforced approach slabs and dock aprons built for trailer loads and constant turning traffic.",
                image: "/images/industrial/industrial-concrete-m2.e2f5c2aa.webp",
                alt: "Industrial loading dock with newly resurfaced concrete dock wall, steel bumpers and rebuilt brick surround.",
            },
            {
                name: "Equipment & machine pads",
                desc: "Isolated, level pads engineered for vibration, anchor loads, and precise tolerances.",
                image: "/images/industrial/industrial-concrete-m3.8e3c524e.webp",
                alt: "Isolated reinforced machine pad with projecting anchor bolts and an isolation joint",
            },
            {
                name: "Repair & resurfacing",
                desc: "Spall repair, joint rebuilds, and overlays to restore worn industrial floors without full replacement.",
                image: "/images/industrial/industrial-concrete-m4.babf2411.webp",
                alt: "Saw-cut spall repair patched flush into a worn industrial concrete floor joint",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We review loads, existing slab condition, sub-grade, and drainage, and confirm tolerances and equipment requirements." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price scope with slab thickness, reinforcement, mix strength, and joint layout designed to your loads." },
            { num: "03", title: "Scheduled Execution", body: "Pours phased and cured around your operations so live areas reopen on a predictable, low-downtime timeline." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Mix designs, strength results, and joint as-builts delivered for your facility and warranty records." },
        ],
        faqs: [
            {
                q: "How thick should an industrial concrete slab be?",
                a: "There's no single answer — it's driven by your loads. A floor carrying forklifts and racking point loads needs more thickness and reinforcement than a light-duty slab, and exterior aprons taking trailer loads need more again. We size thickness, rebar, and sub-base from your equipment weights and traffic, then confirm it in the engineered scope rather than defaulting to a standard depth.",
            },
            {
                q: "Why does industrial concrete spall or crack at the surface?",
                a: "Two common causes: a mix that isn't air-entrained or strong enough for freeze-thaw, and joints that aren't placed to control where the slab cracks. De-icing salts and standing water make scaling worse. We use spec-strength, air-entrained mixes and an engineered joint layout, and we cure properly — uncontrolled drying is a major cause of early surface failure.",
            },
            {
                q: "Can you pour around our ongoing operations?",
                a: "Yes. We phase the work into sections, protect adjacent live areas, and schedule pours and cure windows around your shifts so you're never fully shut down. Concrete needs real cure time before it takes load, so we'll lay out exactly when each section can reopen in the proposal so your team can plan.",
            },
            {
                q: "Do you provide insurance and WSIB documentation for our records?",
                a: "Yes — we provide a Certificate of Insurance naming your entity for commercial general liability, and WSIB clearance certificates on request. For industrial facilities we'll complete your contractor orientation and align with site safety requirements before any work starts.",
            },
            {
                q: "Can you repair our existing slab instead of replacing it?",
                a: "Often, yes. Localized spalling, worn joints, and surface deterioration can frequently be addressed with repair, joint rebuilds, or an overlay rather than full removal — which means far less downtime and cost. We assess whether the base is still sound during the site assessment and recommend repair versus replacement honestly.",
            },
        ],
    },

    "site-security-fencing": {
        slug: "site-security-fencing",
        category: "Industrial · Security Fencing",
        name: "Site Security Fencing",
        heroTitle: "Perimeter security that holds the line.",
        heroSubhead:
            "Heavy-gauge security fencing, gates, and access control to secure industrial yards, compounds, and critical sites across the GTA.",
        heroImage: "/images/industrial/site-security-fencing-hero.e5524e91.webp",
        heroImageAlt: "Galvanized chain-link perimeter with barbed-wire toppers and a double swing gate securing a paved industrial storage yard behind a brick building",
        intro:
            "An industrial perimeter has one job: control who and what gets in. We install heavy-gauge fencing, anti-climb systems, and load-rated gates engineered to your security and operational needs — sized for the vehicles that pass through and built to resist the abuse a working yard delivers. Every install is documented for your security and insurance files.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Heavy-gauge, anti-climb systems",
                body: "Commercial-grade mesh and palisade options with anti-climb and anti-cut detailing for genuine deterrence.",
            },
            {
                icon: "Hammer",
                title: "Frost-set, vehicle-rated posts",
                body: "Posts set below the frost line in concrete and sized for gate loads — no leaning runs or sagging gates.",
            },
            {
                icon: "Award",
                title: "Load-rated gates & access",
                body: "Swing, slide, and cantilever gates matched to your traffic, with operators and access control integration.",
            },
            {
                icon: "Clock",
                title: "Secured-throughout install",
                body: "We phase the work so your perimeter is never left open overnight during the transition.",
            },
        ],
        materials: [
            {
                name: "High-security mesh & palisade",
                desc: "Heavy-gauge welded mesh or steel palisade with anti-climb detailing for hard perimeters.",
                image: "/images/industrial/site-security-fencing-m1.aee93073.webp",
                alt: "Heavy-gauge anti-climb welded mesh security fencing with a palisade section beyond",
            },
            {
                name: "Commercial chain-link",
                desc: "Galvanized or PVC-coated chain-link with top rail and barbed or razor toppers where required.",
                image: "/images/industrial/site-security-fencing-m2.9080cb6d.webp",
                alt: "Long black PVC-coated chain-link perimeter fence with top rail running along a property boundary at dusk",
            },
            {
                name: "Gates & operators",
                desc: "Slide, cantilever, and swing gates with automated operators sized to your vehicle traffic.",
                image: "/images/industrial/site-security-fencing-m3.1ad3eab2.webp",
                alt: "Galvanized cantilever slide gate open across an industrial yard entrance with its operator",
            },
            {
                name: "Access control integration",
                desc: "Keypad, card, and intercom integration coordinated with your existing security systems.",
                image: "/images/industrial/site-security-fencing-m4.4d6df434.webp",
                alt: "Weatherproof card reader and intercom pedestal at an industrial vehicle entrance",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We walk the perimeter, mark lot lines and utilities, and assess vehicle traffic, access points, and security priorities." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price scope with fence type, post spacing, gate sizing, and access control engineered to your site." },
            { num: "03", title: "Scheduled Execution", body: "Installed in phases that keep the perimeter secured throughout the transition with minimal disruption to access." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Layout drawings, gate specs, and access-control documentation delivered for your security and insurance files." },
        ],
        faqs: [
            {
                q: "What type of fencing is best for securing an industrial yard?",
                a: "It depends on the threat level and what you're protecting. Heavy-gauge welded mesh and steel palisade give the strongest anti-climb, anti-cut deterrence for high-value or critical sites, while commercial chain-link with a top rail and toppers is a cost-effective perimeter for general yards. We recommend the system based on your security priorities, vehicle traffic, and budget during the assessment.",
            },
            {
                q: "Can you keep our site secure while you replace the fence?",
                a: "Yes — that's a core part of how we plan industrial fence work. We phase the install so an open run is closed or temporarily secured before the crew leaves each day, and your perimeter is never left exposed overnight during the transition. We'll detail the phasing and any temporary measures in the proposal.",
            },
            {
                q: "Do you install and integrate automated gates and access control?",
                a: "Yes. We install slide, cantilever, and swing gates with operators sized to your traffic, and we coordinate with keypad, card-reader, intercom, and existing security systems. We size the gate and operator to vehicle frequency and width so it holds up to constant industrial use rather than wearing out prematurely.",
            },
            {
                q: "Will heaving or vehicle impact loosen the posts over time?",
                a: "Not when posts are set correctly. We set posts below the frost line in concrete and size gate and corner posts for the loads they carry, which prevents the leaning runs and sagging gates you see when posts are undersized or set too shallow. Vehicle-exposed corners can also be protected with bollards, which we can include in scope.",
            },
            {
                q: "Are you insured and WSIB-compliant to work on our industrial site?",
                a: "Yes. We provide a Certificate of Insurance naming your entity and WSIB clearance certificates on request, and we complete site-specific contractor orientation and safety requirements before mobilizing on industrial and secured sites across the GTA.",
            },
        ],
    },

    "industrial-masonry-structural": {
        slug: "industrial-masonry-structural",
        category: "Industrial · Masonry & Structural",
        name: "Industrial Masonry & Structural Repair",
        heroTitle: "Restore structure. Stop the deterioration.",
        heroSubhead:
            "Masonry restoration, structural repair, and concrete remediation for industrial and commercial buildings across the GTA — done to spec, documented for your records.",
        heroImage: "/images/industrial/industrial-masonry-structural-hero.d84f71f3.webp",
        heroImageAlt: "Rebuilt loading dock base with fresh parge coating and new overhead door in red brick wall",
        intro:
            "Cracked block, spalling concrete, failing lintels, and deteriorating brick aren't cosmetic — left alone they let water in and compromise the structure. We assess the cause, not just the symptom, then repair with compatible materials and load-aware methods so the fix lasts. All work is sequenced to minimize downtime and documented for your asset and compliance files.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Cause-first assessment",
                body: "We identify why masonry is failing — water, movement, load — so the repair addresses the source, not just the surface.",
            },
            {
                icon: "Hammer",
                title: "Load-aware structural repair",
                body: "Lintel replacement, crack stitching, and shoring done with the structure's loads in mind, coordinated with engineers where required.",
            },
            {
                icon: "Award",
                title: "Compatible materials",
                body: "Mortar and units matched to the existing assembly so repairs bond, breathe, and weather correctly.",
            },
            {
                icon: "Clock",
                title: "Minimal-disruption sequencing",
                body: "Work zoned and staged so occupied or operating areas of the building keep running.",
            },
        ],
        materials: [
            {
                name: "Brick & block restoration",
                desc: "Tuckpointing, unit replacement, and repointing with mortar matched to the existing assembly.",
                image: "/images/industrial/industrial-masonry-structural-m1.f2959974.webp",
                alt: "Repointed industrial brick wall with fresh mortar joints meeting older weathered work",
            },
            {
                name: "Concrete remediation",
                desc: "Spall repair, rebar treatment, and patching to stop deterioration in walls, columns, and structures.",
                image: "/images/industrial/industrial-masonry-structural-m2.62a8d304.webp",
                alt: "Severely spalled concrete block pier at a dock opening cut back for structural rebuild",
            },
            {
                name: "Lintel & opening repair",
                desc: "Replacement of failing steel lintels and rebuild of door, window, and dock openings.",
                image: "/images/industrial/industrial-masonry-structural-m3.127241a4.webp",
                alt: "Loading dock opening being rebuilt with new brick jambs and patched concrete dock face",
            },
            {
                name: "Crack repair & sealing",
                desc: "Crack stitching, injection, and weather sealing to stop water ingress and arrest movement.",
                image: "/images/industrial/industrial-masonry-structural-m4.453f5dd3.webp",
                alt: "Crack-stitched block wall with helical bars bedded in joints and a sealed control joint",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We inspect the failing masonry and structure, identify root causes, and flag anything requiring an engineer's input." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price scope detailing repair methods, materials, and any structural coordination, with a clear sequence." },
            { num: "03", title: "Scheduled Execution", body: "Repairs zoned and staged around occupied and operating areas to keep the building running with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Repair records, material specs, and engineer sign-offs where applicable delivered for your asset files." },
        ],
        faqs: [
            {
                q: "How do I know if cracking masonry is structural or cosmetic?",
                a: "Hairline cracks in mortar are often cosmetic, but stair-step cracking through brick, widening cracks, displaced units, or cracks near lintels and openings can indicate movement or load issues that need structural attention. We assess crack patterns, width, and location during the site visit and bring in a structural engineer when the evidence warrants it — we don't guess on structure.",
            },
            {
                q: "Why does spalling concrete keep coming back after patching?",
                a: "Usually because the patch treated the surface but not the cause. When embedded rebar corrodes it expands and pushes the concrete off — if the steel isn't cleaned and treated, the new patch fails the same way. Water ingress and freeze-thaw drive it. We treat the rebar and address the water source, which is why our repairs hold rather than recurring each year.",
            },
            {
                q: "Can you do masonry repair on an occupied or operating building?",
                a: "Yes. We zone and stage the work so occupied offices and operating production areas keep running, set up containment and protection where needed, and schedule disruptive tasks around your hours. We'll lay out the phasing in the proposal so your facility team knows exactly what's affected and when.",
            },
            {
                q: "Do you coordinate with structural engineers?",
                a: "Yes — and we insist on it where the work involves structural load. For lintel replacement, shoring, or anything where capacity is in question, we coordinate with a structural engineer for design and sign-off, and we include their documentation in the records we hand over. This protects you on both compliance and warranty.",
            },
            {
                q: "Are you insured and WSIB-compliant for this type of work?",
                a: "Yes. We provide a Certificate of Insurance naming your entity for commercial general liability and WSIB clearance certificates on request, and we follow site-specific safety requirements — including work-at-height and containment protocols — on commercial and industrial buildings across Brampton, Mississauga, Toronto, and the GTA.",
            },
        ],
    },

    "retaining-walls-site-works": {
        slug: "retaining-walls-site-works",
        category: "Industrial · Site Works",
        name: "Retaining Walls & Site Works",
        heroTitle: "Hold the grade. Protect the asset.",
        heroSubhead:
            "Engineered retaining walls, grading, and site works that stabilize industrial sites and protect your infrastructure across the GTA.",
        heroImage: "/images/industrial/retaining-walls-site-works-hero.f791a4bd.webp",
        heroImageAlt: "Timber retaining wall with tiebacks holding a regraded slope beside chain-link and new cedar fencing",
        intro:
            "Retaining structures fail when they're undersized for the soil and water behind them. We build engineered walls and reshape grades to manage soil loads, drainage, and surcharge from traffic and structures — so your slopes hold, your pavement stays put, and water moves where it should. All work is documented for your asset and compliance records.",
        valueBullets: [
            {
                icon: "Hammer",
                title: "Engineered for soil & surcharge",
                body: "Wall design accounts for soil loads, water pressure, and traffic surcharge — not just height.",
            },
            {
                icon: "ShieldCheck",
                title: "Drainage built in",
                body: "Drainage stone, weeping systems, and proper backfill so hydrostatic pressure never builds behind the wall.",
            },
            {
                icon: "Award",
                title: "Compacted, graded base",
                body: "Foundations and grades built on compacted base so walls and site surfaces don't settle or shift.",
            },
            {
                icon: "Clock",
                title: "Coordinated site sequencing",
                body: "Earthworks, walls, and surface restoration sequenced to keep the rest of your site operational.",
            },
        ],
        materials: [
            {
                name: "Segmental block walls",
                desc: "Engineered modular block retaining walls with geogrid reinforcement for taller or loaded slopes.",
                image: "/images/industrial/retaining-walls-site-works-m1.348a1194.webp",
                alt: "Engineered modular block retaining wall with drainage stone at an industrial grade change",
            },
            {
                name: "Poured & block structural walls",
                desc: "Reinforced concrete and masonry walls for high-load, high-surcharge retaining applications.",
                image: "/images/industrial/retaining-walls-site-works-m2.b24d8e09.webp",
                alt: "Poured reinforced concrete foundation walls and columns with exposed rebar on a graded construction site",
            },
            {
                name: "Grading & earthworks",
                desc: "Cut, fill, and re-grading to establish stable slopes and positive drainage across the site.",
                image: "/images/industrial/retaining-walls-site-works-m3.0280a531.webp",
                alt: "Compact skid-steer loader on a stripped and re-graded yard beside a new concrete drive, with fresh track marks and windrowed soil",
            },
            {
                name: "Drainage & backfill systems",
                desc: "Weeping tile, drainage stone, and engineered backfill to relieve water pressure behind structures.",
                image: "/images/industrial/retaining-walls-site-works-m4.77726a59.webp",
                alt: "Timber retaining wall lined with black filter fabric, with weeping tile at its base and clear drainage stone staged for backfill",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We survey grades, soils, drainage patterns, and surcharge loads, and identify any movement or instability." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price scope with wall type, reinforcement, drainage, and grading designed to the loads and engineered where required." },
            { num: "03", title: "Scheduled Execution", body: "Earthworks and wall construction sequenced to keep the rest of your site operational with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Engineering, compaction, and drainage records delivered for your asset and warranty files." },
        ],
        faqs: [
            {
                q: "When does a retaining wall need to be engineered?",
                a: "As a rule of thumb, walls above roughly a metre, walls carrying surcharge loads from traffic or structures, and walls retaining problematic soils typically need engineered design — and many GTA municipalities require it and a permit above set heights. We assess height, load, and soil conditions on site and bring in engineering where it's needed for both safety and approval.",
            },
            {
                q: "Why do retaining walls lean, bulge, or fail?",
                a: "The most common cause is water. Without drainage behind the wall, hydrostatic pressure builds up and pushes it over — and undersized reinforcement or poor backfill compaction make it worse. We design drainage stone, weeping systems, and proper backfill into every wall and size the structure for the soil and surcharge, which is what prevents the leaning and bulging you see on failed walls.",
            },
            {
                q: "Can you fix drainage and grading problems flooding our site?",
                a: "Yes — that's core site-works scope. We survey how water currently moves, then re-grade to establish positive drainage away from buildings and pavement, and add drainage systems where needed. Standing water and erosion are usually grade and drainage problems, and correcting them protects your pavement, foundations, and retaining structures from premature failure.",
            },
            {
                q: "Can you do site works without shutting down our operations?",
                a: "In most cases, yes. We sequence earthworks, wall construction, and surface restoration to keep your access routes and operating areas usable, phasing the work area so you're not fully shut down. We'll map out the staging and any temporary access changes in the proposal.",
            },
            {
                q: "Are you insured and WSIB-compliant for excavation and site work?",
                a: "Yes. We provide a Certificate of Insurance naming your entity and WSIB clearance certificates on request, and we follow excavation and trench safety requirements and locate all utilities before digging on industrial sites across the GTA.",
            },
        ],
    },

    "bollards-guard-rails-safety": {
        slug: "bollards-guard-rails-safety",
        category: "Industrial · Traffic Safety",
        name: "Bollards, Guard Rails & Traffic Safety",
        heroTitle: "Protect people, racking, and infrastructure.",
        heroSubhead:
            "Impact-rated bollards, guard rails, and traffic protection that shield your assets and keep your site compliant across the GTA.",
        heroImage: "/images/industrial/bollards-guard-rails-safety-hero.588552ca.webp",
        heroImageAlt: "Long yellow steel guard rail shielding a bank of gas meters along an industrial building",
        intro:
            "One forklift or trailer strike to an unprotected column, dock, or gas line can shut a facility down. We install impact-rated bollards, guard rails, and traffic protection sized for the vehicles on your site — concrete-set and positioned to take the hit so your infrastructure and people don't. All work is documented for your safety and compliance records.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Impact-rated protection",
                body: "Bollards and rails sized and set to absorb forklift and vehicle strikes at your site's real speeds and loads.",
            },
            {
                icon: "Hammer",
                title: "Concrete-set, properly anchored",
                body: "Embedded and core-drilled installations done right so a real impact doesn't simply uproot the post.",
            },
            {
                icon: "Award",
                title: "Asset & life-safety focus",
                body: "Protection placed to shield columns, racking, dock equipment, gas lines, and pedestrian routes.",
            },
            {
                icon: "Clock",
                title: "Fast, low-disruption installs",
                body: "Layouts installed in tight windows so traffic lanes and docks reopen quickly.",
            },
        ],
        materials: [
            {
                name: "Steel bollards",
                desc: "Concrete-filled, embedded or surface-mounted steel bollards to protect columns, gates, and equipment.",
                image: "/images/industrial/bollards-guard-rails-safety-m1.5178d61f.webp",
                alt: "Two yellow steel bollards protecting a gas meter and regulator beside a loading dock door",
            },
            {
                name: "Guard & guide rails",
                desc: "Steel guard rail and barrier systems to channel traffic and protect racking, walls, and pedestrians.",
                image: "/images/industrial/bollards-guard-rails-safety-m2.11717932.webp",
                alt: "Yellow steel guard rail protecting pallet racking and wall from forklift impact",
            },
            {
                name: "Dock & equipment protection",
                desc: "Bumpers, corner guards, and barriers shielding dock levelers, doors, and fixed equipment from strikes.",
                image: "/images/industrial/bollards-guard-rails-safety-m3.cf4ed589.webp",
                alt: "Black rubber dock bumpers and steel jamb guards protecting a warehouse loading dock door",
            },
            {
                name: "Pedestrian & traffic delineation",
                desc: "Painted walkways, barriers, and signage to separate forklift routes from foot traffic.",
                image: "/images/industrial/bollards-guard-rails-safety-m4.c01fb707.webp",
                alt: "Painted pedestrian walkway separated from a forklift aisle by yellow barrier posts",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We walk your traffic routes, docks, and pinch points to identify exposed assets and pedestrian conflicts." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price layout specifying bollard and rail types, anchoring, and placement sized to your vehicles." },
            { num: "03", title: "Scheduled Execution", body: "Installed in tight windows so traffic lanes and docks reopen quickly with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Layout drawings and product specs delivered for your safety, insurance, and compliance files." },
        ],
        faqs: [
            {
                q: "How do I choose the right bollard for forklift and truck protection?",
                a: "It comes down to the impact you need to stop — vehicle weight, speed, and what's behind the bollard. A bollard protecting a structural column or a gas line needs more diameter, wall thickness, and embedment than one marking a walkway. We size the bollard and its anchoring to your site's actual vehicles and speeds during the assessment rather than installing a generic post.",
            },
            {
                q: "Should bollards be embedded in concrete or surface-mounted?",
                a: "Embedded, concrete-set bollards take far more impact and are the right choice for serious asset and life-safety protection. Surface-mounted bollards are quicker and work where you can't excavate or need removability, but they rely on anchors that can shear under a hard strike. We recommend the method based on the impact risk and what's below grade at each location.",
            },
            {
                q: "Can bollards and guard rails help with our health and safety compliance?",
                a: "Yes. Separating forklift and vehicle traffic from pedestrians, and protecting hazards like gas meters, electrical equipment, and racking, supports your obligations under Ontario occupational health and safety requirements. We document the layout and product specs so you have records for your safety program and insurer, though we always recommend confirming specifics with your H&S advisor.",
            },
            {
                q: "How quickly can you install protection without disrupting our operations?",
                a: "Bollard and rail installs are typically fast and easy to phase. We schedule core drilling and concrete work in tight windows around your shifts and reopen lanes and docks as quickly as cure times allow. For urgent protection after a strike, we can often prioritize the critical locations first and complete the rest on a follow-up visit.",
            },
            {
                q: "Are you insured and WSIB-compliant to work in our facility?",
                a: "Yes. We provide a Certificate of Insurance naming your entity and WSIB clearance certificates on request, and we complete contractor orientation and follow site-specific safety rules — including working around live traffic and equipment — at industrial sites across the GTA.",
            },
        ],
    },

    "exterior-grounds-drainage": {
        slug: "exterior-grounds-drainage",
        category: "Industrial · Grounds & Drainage",
        name: "Exterior Grounds & Drainage",
        heroTitle: "Keep water off your assets and grounds in order.",
        heroSubhead:
            "Grounds maintenance, drainage, and exterior upkeep that protect industrial sites from water damage and keep them presentable across the GTA.",
        heroImage: "/images/industrial/exterior-grounds-drainage-hero.fdc1a68a.webp",
        heroImageAlt: "Industrial yard graded to a catch basin with trench drain and swale carrying water away",
        intro:
            "Water is the single biggest threat to industrial site infrastructure — it undermines pavement, floods docks, and erodes grades. We manage exterior grounds and engineer drainage so water moves away from your buildings and pavement, while keeping the property's exterior orderly and compliant. All work is documented and built around your operations to minimize downtime.",
        valueBullets: [
            {
                icon: "ShieldCheck",
                title: "Water moved away from assets",
                body: "Catch basins, swales, and grading directed to protect pavement, docks, and foundations from standing water.",
            },
            {
                icon: "Hammer",
                title: "Built-in subsurface drainage",
                body: "Weeping tile, French drains, and culverts installed to relieve saturated grounds and chronic wet spots.",
            },
            {
                icon: "Award",
                title: "Presentable, controlled grounds",
                body: "Vegetation, edges, and exterior areas kept orderly so your site reads as a well-run operation.",
            },
            {
                icon: "Clock",
                title: "Scheduled around operations",
                body: "Maintenance and drainage work timed to your site activity so freight and access aren't interrupted.",
            },
        ],
        materials: [
            {
                name: "Surface drainage",
                desc: "Catch basins, trench drains, and swales to capture and route surface water off the site.",
                image: "/images/industrial/exterior-grounds-drainage-m1.0c67e6ed.webp",
                alt: "New cast-iron catch basin set flush in an asphalt yard with a trench drain beyond",
            },
            {
                name: "Subsurface drainage",
                desc: "Weeping tile, French drains, and culverts to relieve saturated ground and chronic wet areas.",
                image: "/images/industrial/exterior-grounds-drainage-m2.77726a59.webp",
                alt: "Clear drainage stone, geotextile filter fabric and a black drain pipe installed behind a new timber retaining wall",
            },
            {
                name: "Grounds maintenance",
                desc: "Vegetation control, edging, and exterior upkeep to keep the property orderly and compliant.",
                image: "/images/industrial/exterior-grounds-drainage-m3.6081a23b.webp",
                alt: "Tidy industrial fence line with vegetation cut back and a cleanly edged gravel strip",
            },
            {
                name: "Erosion & grade control",
                desc: "Re-grading, stone, and stabilization to stop erosion and protect slopes and pavement edges.",
                image: "/images/industrial/exterior-grounds-drainage-m4.0280a531.webp",
                alt: "Compact skid steer re-grading soil across a stripped yard beside a new concrete walkway",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We map drainage patterns, standing-water and erosion problems, and grounds conditions across the property." },
            { num: "02", title: "Engineered Scope & Quote", body: "A fixed-price scope with drainage solutions, grading, and a grounds maintenance plan matched to your site." },
            { num: "03", title: "Scheduled Execution", body: "Work and recurring maintenance timed around site activity to keep freight and access moving with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Drainage as-builts and maintenance records delivered for your facility and compliance files." },
        ],
        faqs: [
            {
                q: "Why does water keep pooling on our industrial property?",
                a: "Pooling is almost always a grade or drainage capacity problem — the surface doesn't slope to a collection point, or the catch basins and pipes can't carry the volume. Settlement, clogged systems, and added pavement over the years all contribute. We map how water currently moves during the assessment and correct grades and drainage capacity so it has somewhere to go.",
            },
            {
                q: "How does poor drainage damage pavement and buildings?",
                a: "Standing water saturates the granular base under pavement, which then fails under heavy loads — rutting, cracking, and potholes. Against buildings it undermines foundations and floods docks and low entries, and in winter it refreezes into hazards. Correcting drainage is one of the highest-return investments you can make in protecting the assets you've already paid for.",
            },
            {
                q: "Can you handle ongoing grounds maintenance across multiple sites?",
                a: "Yes. We provide recurring grounds maintenance and drainage upkeep across Brampton, Mississauga, Toronto, and the wider GTA, and we regularly service multi-site portfolios under a single point of contact with consolidated scheduling and documentation. That keeps your sites consistent and your reporting simple.",
            },
            {
                q: "Will drainage and grounds work interrupt our operations?",
                a: "We schedule around your site activity to avoid it. Recurring maintenance is timed to low-traffic windows, and larger drainage installs are phased so access routes and operating areas stay usable. We'll outline the schedule and any temporary access changes in the proposal so your team can plan.",
            },
            {
                q: "Are you insured and WSIB-compliant for grounds and drainage work?",
                a: "Yes. We provide a Certificate of Insurance naming your entity and WSIB clearance certificates on request, and we locate utilities before excavation and follow site safety requirements on industrial properties across the GTA.",
            },
        ],
    },

    "snow-ice-management-industrial": {
        slug: "snow-ice-management-industrial",
        category: "Industrial · Seasonal",
        name: "Snow & Ice Management — Industrial",
        seasonal: true,
        heroTitle: "Keep the site open through every storm.",
        heroSubhead:
            "Industrial-scale snow and ice management that keeps your yards, docks, and routes operating and liability-managed all winter across the GTA.",
        heroImage: "/images/industrial/snow-ice-management-industrial-hero.965f5a43.webp",
        heroImageAlt: "Cleared industrial truck court with loading docks, parked trailers and stockpiled snow",
        intro:
            "For an industrial site, a snowed-in yard means stopped freight and exposed liability. We provide large-scale plowing, de-icing, and snow management built around your operating hours, dock schedules, and traffic flow — with the equipment to clear heavy-acreage lots fast and the documentation to back your liability position. This is a seasonal service contracted ahead of winter.",
        valueBullets: [
            {
                icon: "Clock",
                title: "Operations-driven response",
                body: "Trigger depths, priority routes, and timing built around your shift starts and dock schedules to minimize downtime.",
            },
            {
                icon: "Hammer",
                title: "Heavy-acreage equipment",
                body: "Loaders, plows, and salters sized to clear large industrial yards and laneways quickly, not just small lots.",
            },
            {
                icon: "ShieldCheck",
                title: "Liability-managed de-icing",
                body: "Proactive salting and anti-icing of routes, walkways, and docks to manage slip-and-fall exposure.",
            },
            {
                icon: "Award",
                title: "Time-stamped service records",
                body: "Logged visits, conditions, and materials applied — the documentation your insurer and risk team want.",
            },
        ],
        materials: [
            {
                name: "Large-lot snow plowing",
                desc: "Loader and plow clearing of heavy-acreage yards, truck courts, and laneways sized to your site.",
                image: "/images/industrial/snow-ice-management-industrial-m1.6837340e.webp",
                alt: "Wheel loader with a snow pusher box clearing a large industrial yard",
            },
            {
                name: "De-icing & anti-icing",
                desc: "Salt, brine, and treatment programs for traffic surfaces, walkways, and dock areas.",
                image: "/images/industrial/snow-ice-management-industrial-m2.e661a7db.webp",
                alt: "Brine applicator truck laying anti-icing treatment along an industrial roadway",
            },
            {
                name: "Dock & walkway clearing",
                desc: "Priority hand and machine clearing of docks, entrances, and pedestrian routes for safety and access.",
                image: "/images/industrial/snow-ice-management-industrial-m3.6568b5c9.webp",
                alt: "Cleared and salted loading dock apron with yellow protective bollards and walkway",
            },
            {
                name: "Snow relocation & hauling",
                desc: "Stacking, relocation, and off-site hauling to reclaim parking and yard space through the season.",
                image: "/images/industrial/snow-ice-management-industrial-m4.79c469e4.webp",
                alt: "Wheel loader loading snow into a tandem dump truck beside a snow stockpile",
            },
        ],
        processSteps: [
            { num: "01", title: "Site Assessment", body: "We map your yard, priority routes, docks, snow-storage areas, and operating hours before the season starts." },
            { num: "02", title: "Engineered Scope & Quote", body: "A seasonal service plan with trigger depths, priority sequence, de-icing protocol, and a fixed pricing structure." },
            { num: "03", title: "Scheduled Execution", body: "Storm response timed to your operations so yards, docks, and routes stay open with minimal downtime." },
            { num: "04", title: "Compliance Documentation & Sign-off", body: "Time-stamped service logs, conditions, and materials applied delivered for your risk and insurance files." },
        ],
        faqs: [
            {
                q: "When should we lock in an industrial snow contract for the season?",
                a: "Well before the first snowfall — typically through the fall. Industrial-scale snow management requires us to commit equipment, materials, and routes for the season, and capacity is finite once winter hits. Securing your contract early guarantees your site is on the route with a documented response plan, rather than scrambling for per-call service mid-storm.",
            },
            {
                q: "How do you keep a large yard open during a heavy storm?",
                a: "With the right equipment and a priority plan. We deploy loaders and plows sized for heavy-acreage clearing, work to defined trigger depths so we're on site before snow accumulates, and clear in a priority sequence — main truck routes and docks first — built around your shift starts. For prolonged storms we run continuous service and relocate snow so the yard doesn't choke up.",
            },
            {
                q: "How does snow service help manage our slip-and-fall liability?",
                a: "Two ways: proactive de-icing of traffic surfaces, walkways, and docks reduces the hazards that cause incidents, and our time-stamped service logs document when we attended, the conditions, and the materials applied. That record is exactly what your insurer and risk team want if a claim arises. We always recommend confirming your specific obligations with your insurer, but documented, consistent service is the foundation of a defensible position.",
            },
            {
                q: "What happens to the snow when our yard fills up?",
                a: "We plan snow-storage locations during the pre-season assessment, then stack and relocate snow through the season to preserve parking and maneuvering space. When piles consume too much usable yard or block sightlines, we haul snow off site. We'll define storage areas and the hauling trigger in your service plan so you don't lose operational space as winter goes on.",
            },
            {
                q: "Are you insured and WSIB-compliant for industrial snow operations?",
                a: "Yes. We carry commercial general liability and provide a Certificate of Insurance naming your entity, and we're WSIB-registered with clearance certificates available on request. We operate to site-specific safety requirements when working in your live yard alongside trucks and equipment across the GTA.",
            },
        ],
    },
};

export const INDUSTRIAL_SERVICES_LIST = Object.values(INDUSTRIAL_SERVICES_DATA);
